import { useLayoutEffect, useRef, useState } from 'react';

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
interface Placement {
  left: number;
  top: number;
  docked: boolean;
}

/** View geometry only: use rendered bounds, including the world's current zoom. */
export function useMapSummaryPosition(eventId: string | undefined, layoutKey: string) {
  const mapRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement>({ left: 0, top: 0, docked: true });
  useLayoutEffect(() => {
    const map = mapRef.current;
    const summary = summaryRef.current;
    if (!map || !summary || !eventId) return;
    const place = () => {
      const area = map.getBoundingClientRect();
      const card = summary.getBoundingClientRect();
      const marker = map.querySelector(`[data-map-event="${eventId}"]`);
      if (!marker) return;
      const markerBox = marker.getBoundingClientRect();
      const anchor = {
        x: (markerBox.left + markerBox.right) / 2 - area.left,
        y: (markerBox.top + markerBox.bottom) / 2 - area.top,
      };
      const obstacles: Box[] = Array.from(
        map.querySelectorAll(
          '.p01-map-place, .p01-map-marker, .p01-layer-panel, .p01-map-legend, .p01-map-zoom, .p01-north, .p01-map-caption, .p01-search-result',
        ),
      )
        .filter((node) => node.getClientRects().length > 0)
        .map((node) => {
          const rect = node.getBoundingClientRect();
          const padding = node === marker ? 19 : 6;
          return {
            left: rect.left - area.left - padding,
            top: rect.top - area.top - padding,
            right: rect.right - area.left + padding,
            bottom: rect.bottom - area.top + padding,
          };
        });
      const xs = [
        8,
        area.width - card.width - 8,
        anchor.x - card.width / 2,
        anchor.x + 40,
        anchor.x - card.width - 40,
        ...obstacles.flatMap((box) => [box.right, box.left - card.width]),
      ];
      const ys = [
        8,
        area.height - card.height - 8,
        anchor.y - card.height / 2,
        anchor.y + 40,
        anchor.y - card.height - 40,
        ...obstacles.flatMap((box) => [box.bottom, box.top - card.height]),
      ];
      let best: Placement = { left: 0, top: 0, docked: true };
      let score = Infinity;
      for (const left of xs)
        for (const top of ys) {
          if (
            left < 8 ||
            top < 8 ||
            left + card.width > area.width - 8 ||
            top + card.height > area.height - 8
          )
            continue;
          if (
            obstacles.some(
              (box) =>
                left < box.right &&
                left + card.width > box.left &&
                top < box.bottom &&
                top + card.height > box.top,
            )
          )
            continue;
          const distance = Math.hypot(
            left + card.width / 2 - anchor.x,
            top + card.height / 2 - anchor.y,
          );
          if (distance < score) {
            score = distance;
            best = { left, top, docked: false };
          }
        }
      setPlacement((previous) =>
        previous.left === best.left && previous.top === best.top && previous.docked === best.docked
          ? previous
          : best,
      );
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(map);
    observer.observe(summary);
    for (const node of map.querySelectorAll(
      '.p01-map-place, .p01-layer-panel, .p01-map-legend, .p01-map-zoom, .p01-search-result',
    ))
      observer.observe(node);
    return () => observer.disconnect();
  }, [eventId, layoutKey, placement.docked]);
  return { mapRef, summaryRef, placement };
}
